import { getAuthHeader } from '../authService';
import { API_BASE_URL } from '../../utils/constants';
import { handleApiResponse } from '../../utils/apiInterceptor';

export interface CreateUserRequest {
  firstName: string;
  lastName: string;
  nni: string;
  phoneNumber: string;
  availableBalance: number;
  userType: string;
  username: string;
  password: string;
  bankName: string;
  createdById?: string; // ID of the user who created this user
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T | null;
}

export const createUser = async (userData: CreateUserRequest, creatorId: string): Promise<ApiResponse<any>> => {
  try {
    const authHeader = getAuthHeader();
    
    console.log('=== CREATE USER DEBUG ===');
    console.log('Creator ID:', creatorId);
    console.log('User data:', userData);
    
    if (!creatorId || creatorId === 'undefined' || creatorId === 'null') {
      console.error('❌ Creator ID is invalid:', creatorId);
      return { 
        success: false, 
        message: 'Creator ID not found. Please log out and log in again.', 
        data: null 
      };
    }
    
    const headers = {
      'Content-Type': 'application/json',
      'X-Creator-Id': creatorId,
      ...(authHeader as HeadersInit)
    };
    
    console.log('Request headers:', headers);
    
    const response = await fetch(`${API_BASE_URL}/users`, {
      method: 'POST',
      headers,
      body: JSON.stringify(userData)
    });
    
    // Check for token expiration
    await handleApiResponse(response);
    
    const result = await response.json();
    console.log('✅ Create user response:', result);
    console.log('=== END DEBUG ===');
    
    return result;
  } catch (error: any) {
    console.error('❌ Create user error:', error);
    return { success: false, message: error.message, data: null };
  }
};

export const getMyUsers = async (userId: string): Promise<ApiResponse<any[]>> => {
  try {
    const authHeader = getAuthHeader();
    const response = await fetch(
      `${API_BASE_URL}/users/created-by/${userId}`,
      { headers: authHeader as HeadersInit }
    );
    // Check for token expiration
    await handleApiResponse(response);
    return await response.json();
  } catch (error: any) {
    return { success: false, message: error.message, data: null };
  }
};

export const getUserDebts = async (userId: string): Promise<ApiResponse<any>> => {
  try {
    const authHeader = getAuthHeader();
    const response = await fetch(
      `${API_BASE_URL}/users/${userId}/debts`,
      { headers: authHeader as HeadersInit }
    );
    // Check for token expiration
    await handleApiResponse(response);
    return await response.json();
  } catch (error: any) {
    return { success: false, message: error.message, data: null };
  }
};

export const downloadUserTemplate = async (): Promise<Blob> => {
  const authHeader = getAuthHeader();
  const response = await fetch(
    `${API_BASE_URL}/users/template/download`,
    { headers: authHeader as HeadersInit }
  );
  // Check for token expiration
  await handleApiResponse(response);
  return await response.blob();
};

export const uploadUsers = async (file: File): Promise<ApiResponse<any[]>> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const authHeader = getAuthHeader();
    
    const response = await fetch(`${API_BASE_URL}/users/template/upload`, {
      method: 'POST',
      headers: authHeader as HeadersInit,
      body: formData
    });
    
    // Check for token expiration
    await handleApiResponse(response);
    
    return await response.json();
  } catch (error: any) {
    return { success: false, message: error.message, data: null };
  }
};

export const deleteUser = async (userId: string, creatorId: string): Promise<ApiResponse<null>> => {
  try {
    const authHeader = getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-Creator-Id': creatorId,
        ...(authHeader as HeadersInit)
      }
    });
    
    // Check for token expiration
    await handleApiResponse(response);
    
    return await response.json();
  } catch (error: any) {
    return { success: false, message: error.message, data: null };
  }
};

/** Delete consumer by phone number. Requires TWIZE_ADMIN. */
export const deleteConsumerByPhone = async (
  phoneNumber: string,
  creatorId: string
): Promise<ApiResponse<null>> => {
  try {
    const authHeader = getAuthHeader();
    const response = await fetch(
      `${API_BASE_URL}/users/phone/${encodeURIComponent(phoneNumber)}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Creator-Id': creatorId,
          ...(authHeader as HeadersInit)
        }
      }
    );

    await handleApiResponse(response);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to delete consumer');
    }
    return data;
  } catch (error: any) {
    return {
      success: false,
      message: error.message || 'Failed to delete consumer',
      data: null
    };
  }
};

